/**
 * The Teams page's people table: its filters (a search, teams, "Geblokkeerd",
 * "Met uitzonderingen") and its sort (person, teams, access, last call), as
 * plain functions.
 */

import type { Person, PersonOverride } from "@/lib/api";
import { matchesPerson, personLabel } from "@/lib/people";
import { type SortColumns, sortTime, type SortValue } from "@/lib/table-sort";

/** The people table's sortable columns. */
export type PeopleColumn = "person" | "teams" | "access" | "last";

/** Each column's first order: names from A, the most access first, the latest call first. */
export const PEOPLE_SORT_COLUMNS: SortColumns<PeopleColumn> = {
  person: "asc",
  teams: "asc",
  access: "desc",
  last: "desc",
};

/** The team filter's choice for the people in no team, who get "Iedereen zonder team". */
export const NO_TEAM = "__none__";

/** The people table's filters. */
export interface PeopleFilters {
  search: string;
  /** Team ids, and `NO_TEAM` for the people in none; empty for everyone. */
  teams: string[];
  /** Only the people whose connector access is blocked. */
  blocked: boolean;
  /** Only the people with an exception. */
  withExceptions: boolean;
}

export const NO_PEOPLE_FILTERS: PeopleFilters = {
  search: "",
  teams: [],
  blocked: false,
  withExceptions: false,
};

/** Whether any filter is set: the table then offers to reset them. */
export function hasPeopleFilters(filters: PeopleFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.teams.length > 0 ||
    filters.blocked ||
    filters.withExceptions
  );
}

/** Whether a person is in one of the chosen teams (`NO_TEAM` for none); any team when none is chosen. */
export function inTeams(person: Pick<Person, "teams">, teams: readonly string[]): boolean {
  if (teams.length === 0) {
    return true;
  }
  return (
    person.teams.some((id) => teams.includes(id)) ||
    (person.teams.length === 0 && teams.includes(NO_TEAM))
  );
}

/** The emails with an exception, in lower case. */
export function emailsWithExceptions(overrides: readonly PersonOverride[]): Set<string> {
  return new Set(overrides.map((override) => override.email.toLowerCase()));
}

/** The people the table lists, before the toggles, and after them. */
export interface FilteredPeople<P> {
  /** Those that match the search and the teams: the toggles count among these. */
  matching: P[];
  /** Those that also match the toggles: the rows. */
  shown: P[];
}

/**
 * The people that match the filters: every word of the search in their name
 * or email, in a chosen team, and, when the toggles are on, blocked or with
 * an exception.
 */
export function filterPeople<P extends Pick<Person, "email" | "name" | "teams" | "blocked">>(
  people: readonly P[],
  filters: PeopleFilters,
  withExceptions: ReadonlySet<string>,
): FilteredPeople<P> {
  const matching = people.filter(
    (person) => matchesPerson(person, filters.search) && inTeams(person, filters.teams),
  );
  const shown = matching.filter(
    (person) =>
      (!filters.blocked || person.blocked) &&
      (!filters.withExceptions || withExceptions.has(person.email.toLowerCase())),
  );
  return { matching, shown };
}

/**
 * What a person sorts on in a column: their name (else their email), the
 * names of their teams as shown (none goes last), how much they get
 * (`access`: their group parts and apps, as `personAccessCount` counts them),
 * or their last call (none goes last).
 */
export function peopleSortValue(
  person: Pick<Person, "email" | "name" | "last_call_at">,
  column: PeopleColumn,
  teamNames: readonly string[],
  access = 0,
): SortValue {
  switch (column) {
    case "person":
      return personLabel(person);
    case "teams":
      return teamNames.join(", ");
    case "access":
      return access;
    case "last":
      return sortTime(person.last_call_at);
  }
}
